import React, { useState, useEffect, useCallback } from 'react';
import { INMOCEAN_FILE_SCHEMAS } from '../config/fileSchemas';
import { INMOCEAN_ORDER_SCHEMAS } from '../config/orderFileSchemas';
import FileUploadCard from './FileUploadCard';
import MultiTabFileUpload from './MultiTabFileUpload';
import SmartDropZone from './SmartDropZone';
import * as XLSX from 'xlsx';
import { getFunctions, httpsCallable } from 'firebase/functions';

import ArchivedActualCost from './ArchivedActualCost'; // CHA-338 — parked legacy Actual Cost (Admin → Archive)
import FirstSaleReview from './FirstSaleReview';
import FirstSaleErrorReport from './FirstSaleErrorReport';
import FirstSaleSettings from './FirstSaleSettings';
import FirstSaleVendors from './FirstSaleVendors'; // CHA-362 — vendor program + FS/FS3 compliance
import FirstSaleBeneficiaries from './FirstSaleBeneficiaries'; // CHA-404 — observed beneficiary→factory pairing
import FirstSaleSavings from './FirstSaleSavings'; // CHA-365 — savings tracker
import DataQualityDiagnostic from './DataQualityDiagnostic';
import AnalyticsDashboard from './AnalyticsDashboard';
import MarginAnalysisDashboard from './MarginAnalysisDashboard';
import LogisticsPage from './LogisticsPage';
import InMoceanWorkflowSystem from './InMoceanWorkflowSystem';
import AgentsHome from './AgentsHome';
import WorkHome from './WorkHome';
import PlaybooksHome from './PlaybooksHome';
import AdminLookupManager from './AdminLookupManager';
import PowerBIWorkflow from './PowerBIWorkflow';
import PowerBIBetaWorkflow from './PowerBIBetaWorkflow';
import ActualCostWorkflow from './ActualCostWorkflow';
import ActualCost2 from './ActualCost2'; // CHA-323 — new Imports module (ACE + broker-list duty/freight reconciliation)
import WarehouseBillingWorkflow from './WarehouseBillingWorkflow';
import ProductionTrackingDashboard from './ProductionTrackingDashboard';
import ProductionTrackingBetaDashboard from './ProductionTrackingBetaDashboard';
import ProductionPipelineDashboard from './ProductionPipelineDashboard';
import TariffTrackerDashboard from './TariffTrackerDashboard';
import CpscSettings from './CpscSettings';
import CpscUpload from './CpscUpload';
import CpscManualCoc from './CpscManualCoc';
import CpscExemptions from './CpscExemptions';
import CpscCertLinks from './CpscCertLinks';
import CpscIorConfig from './CpscIorConfig';
import CpscEmailRecipients from './CpscEmailRecipients';
import CpscMappingsViewer from './CpscMappingsViewer';
import CpscDrafts from './CpscDrafts';
import CpscBrokerHandoff from './CpscBrokerHandoff';
import CpscAuditHistory from './CpscAuditHistory';
import CpscTabBar from './CpscTabBar';
// CHA-267 PR-1 — the Create/Manage CPSC nav (behavior-frozen regrouping of the 9 tabs) + the
// sub-route vocabulary (`#/imports/cpsc/<view>`, legacy-id normalization) + the Create landing.
import CpscCreateManageNav, { normalizeCpscView } from './CpscCreateManageNav';
import CpscCreateLanding from './CpscCreateLanding';
import CpscRegister from './CpscRegister'; // CHA-267 PR-3 — Manage → Register (3-way merged list)
import CpscSubmissions from './CpscSubmissions'; // CHA-267 PR-3 — Manage → Submissions (in-flight worklist + checker)
import OrdersDashboard from './OrdersDashboard';
import AlertsDashboard from './AlertsDashboard';
import Production2Dashboard from './Production2Dashboard';
import InventoryDashboard from './InventoryDashboard';
import FinanceDashboard from './FinanceDashboard';
import LoadingSpinner from '../../../shared/components/LoadingSpinner';
import { InMoceanDataService } from '../services/InMoceanDataService';
import { InMoceanOrderDataService } from '../services/InMoceanOrderDataService';
import {
  Building2,
  Home,
  BarChart3,
  CheckSquare,
  Database,
  Settings,
  ChevronRight,
  Shield,
  PieChart,
  FileCheck,
  AlertTriangle,
  Zap,
  Activity,
  Package,
  DollarSign,
  Navigation,
  TrendingUp,
  Ship,
  Cloud,
  FileText,
  Download,
  RefreshCw,
  Clock,
  Receipt,
  Warehouse,
  Mail,
  Factory, // CHA-404 — First Sale → Beneficiaries
} from 'lucide-react';
import RoutingSheetImporter from './RoutingSheetImporter';
// Routing App (multi-tenant SaaS)
import { RoutingDashboard } from '../../../apps/routing';
// CHA-240 — Carbon app shell (chrome-only). Content (renderContent + the
// components it returns) is untouched; only the chrome around it is new.
import Sidebar from './shell/Sidebar';
import { isRouteActive, firstVisibleNavId, isRouteAllowed, isPageGranted, redirectRetiredRoute } from './shell/navConfig';
import TopBar from './shell/TopBar';
import TodayHome from './shell/TodayHome';
// CHA-272 — live shell data (agent-tickets view model + azure sync chip) and
// the sign-out wiring for the sidebar footer.
import useAgentTickets from './shell/useAgentTickets';
import useSyncStatus from './shell/useSyncStatus';
// CHA-276 — canonical Carbon states for the reskinned ImportsPage/DataPage chrome.
import { SkeletonRows, ErrorBanner } from './shell/carbonStates';
import { FirebaseService } from '../../../shared/services/FirebaseService';
import { getProject1818DemoData } from '../../project1818/Project1818DemoService';

// ============================================================================
// URL HASH ROUTING
// ============================================================================
const getHashRoute = () => {
  const hash = window.location.hash.replace('#/', '').replace('#', '');
  // CHA-633 — a retired route (#/workflow, #/reports) resolves to its replacement before anything else.
  return redirectRetiredRoute(hash || 'home');
};

const setHashRoute = (route) => {
  window.location.hash = `#/${route}`;
};

// ============================================================================
// MAIN DASHBOARD COMPONENT
// ============================================================================
export default function InMoceanDashboard({ user, userProfile, tenantConfig }) {
  const tenantId = tenantConfig?.id || userProfile?.tenantId;
  const demoMode = Boolean(tenantConfig?.demoMode);
  const shellBrand = tenantConfig?.shellBrand;
  const navLabelOverrides = tenantConfig?.navLabelOverrides || {};
  // CHA-553 (C): the route a restricted role should land on / be confined to
  // at the top level — its first granted nav item. Un-granted roles and admin
  // resolve to 'home' (they see everything). `null` = an explicit zero-access
  // role (pages: []) with NO landing route: render the no-access state below,
  // never fall back to 'home' (Ron's L3 — that is the very bypass C closes).
  const landingRoute = firstVisibleNavId(userProfile?.pages);
  const hasNoAccess = landingRoute === null;

  // Route state — a restricted role must not default to 'home' (Today) it
  // has no grant for (operator: "no need for Today").
  const [currentRoute, setCurrentRoute] = useState(() => {
    const r = getHashRoute();
    return isRouteAllowed(r, userProfile?.pages) ? r : (landingRoute || 'home');
  });

  // CHA-553 (C, Codex): guard at RENDER time, not only in the post-render
  // effect. If pages change under a stale currentRoute (e.g. a same-account
  // token refresh that drops a grant), the effect redirects only AFTER React
  // has already committed one render of the ungranted route — mounting it and
  // starting its data effects. Deriving the permitted route during render
  // means the ungranted page never renders even for that one frame. The effect
  // still corrects the URL/state; this governs what is shown.
  const effectiveRoute = isRouteAllowed(currentRoute, userProfile?.pages)
    ? currentRoute
    : (landingRoute || 'home');

  // Data state
  const [uploadHistory, setUploadHistory] = useState({});
  const [orderUploadHistory, setOrderUploadHistory] = useState({});
  const [loading, setLoading] = useState(true);
  const [demoData, setDemoData] = useState(null);
  const [demoDataLoading, setDemoDataLoading] = useState(false);
  const [demoDataError, setDemoDataError] = useState(null);
  
  // Duplicate checking state
  const [duplicateChecking, setDuplicateChecking] = useState({});
  const [duplicateResults, setDuplicateResults] = useState({});

  // CHA-402 Slice A - roleId is populated from Auth claims minted from the
  // server-controlled roleAssignments collection. Platform roles are global;
  // a tenant admin is authoritative only inside the tenant in its claim.
  const roleId = userProfile?.roleId;
  const isPlatformAdmin = roleId === 'platform_owner' || roleId === 'platform_admin';
  const isTenantAdmin = roleId === 'admin'
    && Boolean(tenantConfig?.id)
    && userProfile?.tenantId === tenantConfig.id;
  const isAdmin = isPlatformAdmin || isTenantAdmin;

  // CHA-272 — shared shell data: ONE agent-tickets view model feeds the
  // sidebar badge and the Today view (KPI consistency, CHA-239), plus the
  // live AZURE SYNC chip. Both are read-only subscriptions.
  const agentTickets = useAgentTickets({ enabled: !demoMode });
  const liveSyncStatus = useSyncStatus({ enabled: !demoMode });
  const syncStatus = demoMode ? { label: 'DEMO MODE', ok: true } : liveSyncStatus;

  // ============================================================================
  // HASH ROUTE SYNC
  // ============================================================================
  useEffect(() => {
    const resolve = () => {
      const r = getHashRoute();
      // CHA-633 — rewrite a retired hash in the URL bar too, so the address matches what is shown.
      if (!hasNoAccess && window.location.hash.replace('#/', '').replace('#', '') !== r && isRouteAllowed(r, userProfile?.pages)) {
        setHashRoute(r);
      }
      // CHA-553 (C): confine to granted top-level routes. An un-granted route
      // (e.g. a powerbi role at #home or #admin) is redirected to the role's
      // landing route rather than rendered. Base-segment granularity; sub-page
      // and callable enforcement are the rest of C/D.
      // A zero-access role (no landing route) never navigates anywhere — the
      // no-access state renders instead of any route.
      if (hasNoAccess) return;
      if (!isRouteAllowed(r, userProfile?.pages)) {
        setHashRoute(landingRoute);
        setCurrentRoute(landingRoute);
        return;
      }
      setCurrentRoute(r);
    };
    window.addEventListener('hashchange', resolve);
    if (hasNoAccess) {
      // nothing to route to
    } else if (!window.location.hash) {
      setHashRoute(landingRoute);
    } else {
      resolve(); // guard the initial hash too
    }
    return () => window.removeEventListener('hashchange', resolve);
    // landingRoute derives from userProfile.pages; re-run if the role changes.
  }, [landingRoute, hasNoAccess, userProfile?.pages]);

  // ============================================================================
  // DATA LISTENERS
  // ============================================================================
  useEffect(() => {
    if (demoMode) {
      setUploadHistory({});
      setOrderUploadHistory({});
      setLoading(false);
      return undefined;
    }
    const unsubscribe = InMoceanDataService.listenToUploadHistory((historyData) => {
      setUploadHistory(historyData);
      if (loading) setLoading(false);
    });
    const orderUnsubscribe = InMoceanOrderDataService.listenToOrderUploadHistory((historyData) => {
      setOrderUploadHistory(historyData);
    });
    return () => { unsubscribe(); orderUnsubscribe(); };
  }, [demoMode, loading]);

  useEffect(() => {
    let cancelled = false;
    if (!demoMode || tenantId !== 'project1818') {
      setDemoData(null);
      setDemoDataError(null);
      setDemoDataLoading(false);
      return undefined;
    }
    setDemoDataLoading(true);
    setDemoDataError(null);
    getProject1818DemoData(tenantId)
      .then((data) => {
        if (cancelled) return;
        setDemoData(data);
      })
      .catch((error) => {
        if (cancelled) return;
        console.error('project1818 demo data load failed', error);
        setDemoDataError(error);
        setDemoData(null);
      })
      .finally(() => {
        if (!cancelled) setDemoDataLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [demoMode, tenantId]);

  // ============================================================================
  // NAVIGATION
  // ============================================================================
  const navigateTo = useCallback((route) => {
    // CHA-553 (C, Codex): in-app navigation must be guarded too. navigateTo
    // sets currentRoute synchronously, so without this check an ungranted
    // route (e.g. a Data-role's Ignore List calling navigateTo('admin'))
    // would mount for a render before the hashchange guard fires. Redirect
    // an ungranted target to the landing route; a zero-access role goes
    // nowhere (the no-access state renders).
    if (hasNoAccess) return;
    const next = redirectRetiredRoute(route);
    const target = isRouteAllowed(next, userProfile?.pages) ? next : landingRoute;
    setHashRoute(target);
    setCurrentRoute(target);
  }, [hasNoAccess, landingRoute, userProfile?.pages]);

  // ============================================================================
  // FILE HANDLERS
  // ============================================================================
  const handleUploadComplete = (fileType, result, isOrder = false) => {
    const setter = isOrder ? setOrderUploadHistory : setUploadHistory;
    setter((prev) => ({
      ...prev,
      [fileType]: { ...prev[fileType], lastUpload: new Date().toISOString(), status: 'processing', fileName: result.fileName },
    }));
  };

  const handleUploadError = (fileType, error, isOrder = false) => {
    const setter = isOrder ? setOrderUploadHistory : setUploadHistory;
    setter((prev) => ({
      ...prev,
      [fileType]: { ...prev[fileType], status: 'error', error: error.message },
    }));
  };

  const handleCheckDuplicates = async (fileType) => {
    setDuplicateChecking(prev => ({ ...prev, [fileType]: true }));
    try {
      const result = await InMoceanDataService.checkDuplicates(fileType);
      setDuplicateResults(prev => ({ ...prev, [fileType]: result }));
    } catch (error) {
      console.error(`Error checking duplicates for ${fileType}:`, error);
    } finally {
      setDuplicateChecking(prev => ({ ...prev, [fileType]: false }));
    }
  };

  const handleRemoveDuplicates = async (fileType) => {
    if (!window.confirm(`Remove duplicates from ${fileType}?`)) return;
    setDuplicateChecking(prev => ({ ...prev, [fileType]: true }));
    try {
      await InMoceanDataService.removeDuplicates(fileType);
      setDuplicateResults(prev => { const n = { ...prev }; delete n[fileType]; return n; });
      await handleCheckDuplicates(fileType);
    } catch (error) {
      alert(`Error: ${error.message}`);
    } finally {
      setDuplicateChecking(prev => ({ ...prev, [fileType]: false }));
    }
  };

  // ============================================================================
  // NO ACCESS — CHA-553 (C, Ron L3 + Codex): must precede EVERY route return.
  // An explicit zero-access role (pages: []) has no landing route and must see
  // the no-access state, never a route — including the full-page branches
  // below. Placed here so revoking all grants while on data/first-sale-errors
  // (etc.) cannot leave that page mounted.
  // ============================================================================
  if (hasNoAccess) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-6">
        <div className="max-w-md text-center">
          <h1 className="text-lg font-semibold text-slate-800 mb-2">No pages assigned</h1>
          <p className="text-sm text-slate-600 mb-5">
            Your role has no page access. Ask an administrator to grant the pages you need.
          </p>
          {/* CHA-553 (CodeRabbit): this screen renders before the shell, so
              give the user a way out — otherwise a pages:[] role is stuck with
              no sign-out and can't switch accounts without clearing the session. */}
          <button
            onClick={() => FirebaseService.signOut().catch((e) => console.error('no-access: sign-out failed', e))}
            className="px-4 py-2 bg-slate-800 text-white rounded-lg text-sm font-medium hover:bg-slate-900"
          >
            Sign out
          </button>
        </div>
      </div>
    );
  }

  // ============================================================================
  // RENDER FULL-PAGE VIEWS
  // ============================================================================
  // Data > Verification routes
  if (!demoMode && effectiveRoute === 'data/first-sale-errors') {
    return (
      <FirstSaleErrorReport
        onBackToDashboard={() => navigateTo('data')}
        onNavigateToIgnoreList={() => navigateTo('admin')}
      />
    );
  }
  if (!demoMode && effectiveRoute === 'data/data-quality') {
    return <DataQualityDiagnostic onBackToDashboard={() => navigateTo('data')} />;
  }
  if (!demoMode && effectiveRoute === 'data/analytics') {
    return <AnalyticsDashboard onBackToDashboard={() => navigateTo('data')} />;
  }

  // Navigation is now defined in the Carbon shell (shell/navConfig.js) — the
  // route ids are unchanged; only display labels + grouping moved there (CHA-240).

  // ============================================================================
  // RENDER CONTENT
  // ============================================================================
  const renderContent = () => {
    if (demoMode) {
      return <DemoRouteSurface route={effectiveRoute} demoData={demoData} loading={demoDataLoading} error={demoDataError} />;
    }
    // CHA-267 PR-1 — CPSC deep links: any `#/imports/...` sub-route renders ImportsPage with the
    // route so it can resolve the CPSC view (`imports/cpsc/<view>`; legacy tab ids normalize).
    // The shell's isRouteActive/routeTitle already treat sub-routes as their base segment.
    if (effectiveRoute.startsWith('imports/')) {
      return <ImportsPage userProfile={userProfile} tenantId={tenantId} route={effectiveRoute} navigateTo={navigateTo} />;
    }
    switch (effectiveRoute) {
      case 'home':
        return <HomePage navigateTo={navigateTo} />;
      case 'alerts':
        return <AlertsDashboard />;
      case 'agents':
      case 'agents/yulie':
      case 'agents/ella':
        // CHA-273 — the cockpit consumes the SAME shared view model as the
        // sidebar badge and Today (one subscription, one source of truth).
        return <AgentsHome agentTickets={agentTickets} userProfile={userProfile} />;
      case 'work':
        // CHA-588 / W1b — the Work list consumes the SAME shared view model as the Agents cockpit
        // and the sidebar badge (one subscription, one source of truth). Agents route untouched.
        return <WorkHome agentTickets={agentTickets} userProfile={userProfile} />;
      case 'orders':
        // CHA-274 — the Orders Carbon pass joins rows against the shared
        // agent-tickets view model (severity rails / bot marks / drawer callout).
        // CHA-557 (Codex): no `|| 'inmocean'` fallback — the Target upload
        // workflow validates a required tenant and must FAIL CLOSED on a
        // missing one. Tenant config or the resolved profile supplies context;
        // userProfile carries the
        // grants so Orders can gate the Target tab on data/target.
        return <OrdersDashboard agentTickets={agentTickets} tenantId={tenantId} userProfile={userProfile} />;
      case 'production':
        return <Production2Dashboard userProfile={userProfile} />;
      case 'finance':
        return <FinanceDashboard />;
      case 'inventory':
        return <InventoryDashboard />;
      case 'imports':
        return <ImportsPage userProfile={userProfile} tenantId={tenantId} route={effectiveRoute} navigateTo={navigateTo} />;
      case 'logistics':
        return <LogisticsPage userProfile={userProfile} tenantId={tenantId} onBackToDashboard={() => navigateTo('home')} />;
      case 'routing-beta':
        return <RoutingDashboard tenantId={tenantId} />;
      case 'playbooks':
        // CHA-633 — Playbooks is its own Intelligence tab (admin-only). #/workflow and #/reports are
        // retired and redirect to #/work (redirectRetiredRoute), so they never reach this switch.
        return <PlaybooksHome userProfile={userProfile} />;
      case 'data':
        return (
          <DataPage
            uploadHistory={uploadHistory}
            orderUploadHistory={orderUploadHistory}
            onUploadComplete={handleUploadComplete}
            onUploadError={handleUploadError}
            duplicateResults={duplicateResults}
            duplicateChecking={duplicateChecking}
            onCheckDuplicates={handleCheckDuplicates}
            onRemoveDuplicates={handleRemoveDuplicates}
            navigateTo={navigateTo}
            userProfile={userProfile}
          />
        );
      case 'admin':
        return isAdmin ? <AdminLookupManager userProfile={userProfile} tenantId={tenantId} /> : <HomePage navigateTo={navigateTo} />;
      case 'archive':
        // CHA-338 — parked legacy Actual Cost surface (pre-7/1/26), admin-gated like the Admin area.
        return isAdmin ? <ArchivedActualCost /> : <HomePage navigateTo={navigateTo} />;
      default:
        return <HomePage navigateTo={navigateTo} />;
    }
  };

  // ============================================================================
  // LOADING
  // ============================================================================
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  // ============================================================================
  // MAIN LAYOUT — Carbon app shell (chrome only, CHA-240)
  // ============================================================================
  // The shell (Sidebar + TopBar) wraps the SAME content as before. Today
  // (`home`) renders the new <TodayHome/>; every other route renders the
  // existing renderContent() untouched, inside the retained
  // `p-6 max-w-7xl mx-auto` envelope.
  const isToday = effectiveRoute === 'home';
  return (
    <div className="min-h-screen bg-carbon-gray-10 flex text-carbon-ink">
      <Sidebar
        currentRoute={effectiveRoute}
        navigateTo={navigateTo}
        isAdmin={isAdmin}
        user={user}
        userProfile={userProfile}
        agentsBadge={agentTickets.openCount}
        agentsBadgeError={Boolean(agentTickets.error)}
        onSignOut={() =>
          FirebaseService.signOut().catch((e) => console.error('shell: sign-out failed', e))
        }
        brand={shellBrand}
        navLabelOverrides={navLabelOverrides}
        // CHA-273 — dark chrome on the agents route ONLY (Ops concept).
        variant={isRouteActive(effectiveRoute, 'agents') ? 'dark' : 'light'}
      />
      <main className="flex-1 overflow-auto min-w-0">
        <TopBar currentRoute={effectiveRoute} sync={syncStatus} navLabelOverrides={navLabelOverrides} />
        {demoMode ? (
          <div className="p-6 max-w-7xl mx-auto">
            <DemoRouteSurface route={effectiveRoute} demoData={demoData} loading={demoDataLoading} error={demoDataError} />
          </div>
        ) : isToday ? (
          <TodayHome user={user} userProfile={userProfile} navigateTo={navigateTo} today={agentTickets} />
        ) : (
          <div className="p-6 max-w-7xl mx-auto">
            {renderContent()}
          </div>
        )}
      </main>
    </div>
  );
}

// ============================================================================
// HOME PAGE
// ============================================================================
// NOTE (CHA-240): The Carbon shell routes the `home` nav item to <TodayHome/>
// (shell/TodayHome.jsx). HomePage/QuickCard below are RETAINED unchanged because
// renderContent() (the content seam) still returns <HomePage/> for the `default`
// case and the non-admin `admin` fallback, which the ticket requires be left
// byte-identical. They are no longer reachable from the nav.
const DEMO_ROUTE_COPY = {
  home: {
    title: 'Project1818 Command Center',
    eyebrow: 'DEMO SNAPSHOT',
    metrics: [
      ['At-risk orders', '18'],
      ['Open units', '84.2K'],
      ['Agent follow-ups', '7'],
    ],
    rows: ['COO agent: vendor reply due today', 'Target balance: 42 uploads ready', 'Walmart routing: 3 late appointments'],
  },
  orders: {
    title: 'Orders',
    eyebrow: 'TOP 10 RETAILERS',
    metrics: [
      ['Open POs', '312'],
      ['Shipped this week', '44'],
      ['Past cancel', '6'],
    ],
    rows: ['WALMART P18-438201 ready for routing', 'TARGET NOC P18-772910 awaiting carton dims', "MACY'S VDF P18-119482 needs COO review"],
  },
  production: {
    title: 'Production',
    eyebrow: 'FACTORY FLOW',
    metrics: [
      ['Open WIP', '128.6K'],
      ['QC holds', '11'],
      ['Late handoffs', '4'],
    ],
    rows: ['PT-904118 behind MABD by 3 days', 'PT-331870 passed inspection', 'PT-228410 waiting on trim confirmation'],
  },
  inventory: {
    title: 'Inventory',
    eyebrow: 'WAREHOUSE',
    metrics: [
      ['On hand', '241.3K'],
      ['Received SP27', '17.8K'],
      ['Aging holds', '9'],
    ],
    rows: ['TARGET NOC replenishment available', 'ROSS allocation above plan', 'MARSHALLS pack variance under review'],
  },
  'routing-beta': {
    title: 'Walmart Routing',
    eyebrow: 'BETA',
    metrics: [
      ['Appointments', '23'],
      ['Missing MABD', '2'],
      ['Agent drafts', '5'],
    ],
    rows: ['SHP-829104 needs carrier confirmation', 'SHP-501733 appointment moved to Friday', 'SHP-118406 ready for buyer notice'],
  },
  agents: {
    title: 'Agent (Beta)',
    eyebrow: 'EXECUTIVE AGENTS',
    metrics: [
      ['Open tickets', '14'],
      ['Awaiting replies', '5'],
      ['Auto-cleared', '22'],
    ],
    rows: ['Yulie is tracking past-cancel exposure', 'Ella is watching gross-margin drift', 'COO queue has 3 approval decisions'],
  },
  workflow: {
    title: 'Work (Beta)',
    eyebrow: 'WORKBOARD',
    metrics: [
      ['Active workflows', '9'],
      ['Human waits', '4'],
      ['Agent handoffs', '6'],
    ],
    rows: ['Vendor confirmation email sent from ChainTo', 'Broker reply parsed and matched to CI-440128', 'Director agent waiting on VP approval'],
  },
  data: {
    title: 'Target Balance Sheet',
    eyebrow: 'TARGET DATA',
    metrics: [
      ['Ready rows', '424'],
      ['Held assortments', '418'],
      ['Lookup gaps', '5'],
    ],
    rows: ['Carton dims loaded for selected Target styles', 'Assortment component UPCs held for review', 'Upload action disabled in demo mode'],
  },
};

const DemoRouteSurface = ({ route, demoData, loading, error }) => {
  const base = (route || 'home').split('/')[0];
  const liveView = demoData?.surfaces?.[route] || demoData?.surfaces?.[base];
  const liveDataLoaded = Boolean(demoData) && !loading && !error;
  if (liveDataLoaded && !liveView) {
    return (
      <div className="space-y-4">
        <div className="border border-carbon-border bg-carbon-white p-6">
          <div className="font-plex-mono text-[11px] uppercase tracking-[0.14em] text-carbon-gray-50">
            Access restricted
          </div>
          <h1 className="mt-2 text-xl font-semibold text-carbon-ink">Demo data unavailable for this page</h1>
          <p className="mt-2 max-w-xl text-sm text-carbon-gray-60">
            Your role can open this page, but the Project1818 live demo reader did not return a matching data surface.
          </p>
        </div>
      </div>
    );
  }
  const view = liveView || DEMO_ROUTE_COPY[route] || DEMO_ROUTE_COPY[base] || DEMO_ROUTE_COPY.home;
  const records = Array.isArray(view.records) ? view.records : [];
  const columns = records.length ? Object.keys(records[0]).slice(0, 6) : [];
  return (
    <div className="space-y-6">
      <div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="font-plex-mono text-[11px] tracking-[0.14em] uppercase text-carbon-gray-50">
            {view.eyebrow}
          </div>
          {loading && (
            <span className="border border-carbon-border bg-carbon-white px-2 py-1 font-plex-mono text-[10px] uppercase tracking-[0.12em] text-carbon-gray-60">
              Loading demo data
            </span>
          )}
          {liveView && (
            <span className="border border-emerald-200 bg-emerald-50 px-2 py-1 font-plex-mono text-[10px] uppercase tracking-[0.12em] text-emerald-700">
              BigQuery live
            </span>
          )}
        </div>
        <h1 className="mt-2 text-2xl font-semibold text-carbon-ink">{view.title}</h1>
        {error && (
          <p className="mt-2 text-sm text-amber-700">
            Showing the built-in demo fallback while the Project1818 data reader is unavailable.
          </p>
        )}
      </div>
      <div className="grid grid-cols-[repeat(auto-fit,minmax(180px,1fr))] gap-px bg-carbon-border border border-carbon-border">
        {view.metrics.map(([label, value]) => (
          <div key={label} className="bg-carbon-white px-5 py-4">
            <div className="font-plex-mono text-[10px] tracking-[0.12em] uppercase text-carbon-gray-50">{label}</div>
            <div className="mt-2 font-plex-mono text-2xl font-semibold">{value}</div>
          </div>
        ))}
      </div>
      <div className="bg-carbon-white border border-carbon-border divide-y divide-carbon-border">
        {view.rows.map((row) => (
          <div key={row} className="px-5 py-3 text-sm text-carbon-gray-80">{row}</div>
        ))}
      </div>
      {records.length > 0 && (
        <div className="overflow-hidden border border-carbon-border bg-carbon-white">
          <div className="border-b border-carbon-border px-5 py-3">
            <div className="font-plex-mono text-[10px] uppercase tracking-[0.12em] text-carbon-gray-50">
              Demo table sample
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="bg-carbon-gray-10 font-plex-mono text-[10px] uppercase tracking-[0.08em] text-carbon-gray-50">
                <tr>
                  {columns.map((column) => (
                    <th key={column} className="whitespace-nowrap px-4 py-3 font-medium">{column}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-carbon-border">
                {records.map((row, idx) => (
                  <tr key={`${route || 'home'}-${idx}`}>
                    {columns.map((column) => (
                      <td key={column} className="max-w-[220px] truncate whitespace-nowrap px-4 py-3 text-carbon-gray-80">
                        {row[column] ?? '—'}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

const HomePage = ({ navigateTo }) => (
  <div className="space-y-8">
    <div>
      <h1 className="text-2xl font-semibold text-slate-800">Welcome to InMocean</h1>
      <p className="text-slate-500 mt-1">Cost allocation and order management platform</p>
    </div>

    {/* Quick Links */}
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      <QuickCard
        title="Orders"
        description="Order management and tracking"
        icon={TrendingUp}
        color="blue"
        onClick={() => navigateTo('orders')}
      />
      <QuickCard
        title="Production"
        description="Production tracking and management"
        icon={Package}
        color="green"
        onClick={() => navigateTo('production')}
      />
      <QuickCard
        title="Data"
        description="Upload files, Verification"
        icon={Database}
        color="purple"
        onClick={() => navigateTo('data')}
      />
    </div>

    {/* Placeholder for future content */}
    <div className="bg-white rounded-xl border border-dashed border-slate-300 p-12 text-center">
      <Home size={48} className="mx-auto text-slate-300 mb-4" />
      <h3 className="text-lg font-medium text-slate-500">Dashboard Coming Soon</h3>
      <p className="text-slate-400 mt-2">Summary widgets and quick stats will appear here</p>
    </div>
  </div>
);

const QuickCard = ({ title, description, icon: Icon, color, onClick }) => {
  const colors = {
    blue: 'bg-blue-50 text-blue-600 border-blue-100 hover:border-blue-300',
    green: 'bg-green-50 text-green-600 border-green-100 hover:border-green-300',
    purple: 'bg-purple-50 text-purple-600 border-purple-100 hover:border-purple-300',
    slate: 'bg-slate-50 text-slate-600 border-slate-200 hover:border-slate-400',
  };

  return (
    <button
      onClick={onClick}
      className={`p-5 rounded-xl border text-left transition-all ${colors[color]}`}
    >
      <Icon size={28} className="mb-3" />
      <h3 className="font-semibold">{title}</h3>
      <p className="text-xs opacity-70 mt-1">{description}</p>
    </button>
  );
};

// ============================================================================
// PLACEHOLDER PAGE (for Inventory, Imports, Workflow)
// ============================================================================
const PlaceholderPage = ({ title, icon: Icon, description }) => (
  <div className="space-y-6">
    <div>
      <h1 className="text-2xl font-semibold text-slate-800">{title}</h1>
      <p className="text-slate-500 mt-1">{description}</p>
    </div>

    <div className="bg-white rounded-xl border border-dashed border-slate-300 p-12 text-center">
      <Icon size={48} className="mx-auto text-slate-300 mb-4" />
      <h3 className="text-lg font-medium text-slate-500">{title} Coming Soon</h3>
      <p className="text-slate-400 mt-2">This feature is under development</p>
    </div>
  </div>
);

// ============================================================================
// IMPORTS PAGE - Two main sections: Actual Cost and First Sale
// ============================================================================
// CHA-276 — Carbon class shorthands shared by the in-file ImportsPage/DataPage
// chrome (radius 0, Plex, carbon-* tokens). Presentation only.
const C_TH = 'px-3 py-2 font-plex-mono text-[10px] tracking-[0.12em] uppercase font-normal text-carbon-gray-50 border-b border-carbon-border';
const C_MONO = 'font-plex-mono text-[11.5px]';
// Top-level module tabs (Modules concept underline tabs).
const carbonTabCls = (on) =>
  `flex items-center gap-1.5 px-[18px] py-2.5 text-[13.5px] -mb-px border-b-2 ${
    on ? 'text-carbon-ink font-semibold border-carbon-blue-60' : 'text-carbon-gray-50 border-transparent hover:text-carbon-ink'
  }`;
// Second-level sub-tabs (smaller, on the gray-10 strip).
const carbonSubTabCls = (on) =>
  `flex items-center gap-1.5 px-4 py-2 text-[12.5px] -mb-px border-b-2 ${
    on ? 'text-carbon-ink font-semibold border-carbon-blue-60 bg-carbon-white' : 'text-carbon-gray-50 border-transparent hover:text-carbon-ink'
  }`;

export const ImportsPage = ({ userProfile, tenantId, route, navigateTo }) => {
  // CPSC Settings is an admin/secrets surface — keep its tab out of non-admin navigation, not just
  // blocked after render (CodeRabbit). Server still enforces via requireTenantAdminOrPlatformAdmin.
  // admin || platform_owner — matches the sibling CPSC tabs' internal gates. platform_admin is
  // intentionally NOT added: the callables accept it, but CpscSettings rejects it internally,
  // so exposing the tabs to platform_admin would render a "restricted" message (Codex). Making
  // platform_admin consistent across ALL cpsc surfaces is a separate cleanup — filed as a follow-on.
  const canManageCpscSettings = userProfile?.role === 'admin' || userProfile?.role === 'platform_owner';
  // Derive the CPSC section/view synchronously from `route` on mount (CodeRabbit) so a direct deep
  // link (e.g. `#/imports/cpsc/drafts`) paints the right surface immediately — no one-paint flash of
  // Actual Cost / Create before the sync effect below corrects it.
  const initialCpscView = (() => {
    const r = String(route || '');
    if (!canManageCpscSettings || !(r === 'imports/cpsc' || r.startsWith('imports/cpsc/'))) return null;
    return normalizeCpscView(r.slice('imports/cpsc'.length)) || 'create';
  })();
  // Main section: 'actual-cost-2' (primary Actual Cost) or 'first-sale'
  const [activeSection, setActiveSection] = useState(initialCpscView ? 'cpsc' : 'actual-cost-2');
  // Sub-tabs for Actual Cost
  // Sub-tabs for First Sale
  const [firstSaleTab, setFirstSaleTab] = useState('review');
  // CHA-267 PR-1 — the CPSC view under the Create/Manage IA (was the 9-tab `cpscTab`). Every view
  // is deep-linkable as `#/imports/cpsc/<view>`; legacy tab ids (bv-upload, status-checker, …)
  // normalize to their new homes. Default: the Create landing. Settings keeps its third-level strip.
  const [cpscView, setCpscView] = useState(initialCpscView || 'create');
  const [cpscSettingsTab, setCpscSettingsTab] = useState('cpsc-settings');

  // Parse an incoming CPSC deep link (initial load + hash changes). Admin-gated: for a non-admin
  // the CPSC section simply never activates (same posture as the tab entry below); the server
  // still enforces on every callable regardless.
  useEffect(() => {
    const r = String(route || '');
    // Back/forward OUT of CPSC: the hash returns to bare `imports` — leave the CPSC section too
    // (functional update so a section the user just picked via selectSection isn't clobbered).
    if (r === 'imports') { setActiveSection((s) => (s === 'cpsc' ? 'actual-cost-2' : s)); return; }
    // Exact segment boundary: `imports/cpsc` or `imports/cpsc/...` only (never `imports/cpscx`).
    if (!(r === 'imports/cpsc' || r.startsWith('imports/cpsc/')) || !canManageCpscSettings) return;
    const view = normalizeCpscView(r.slice('imports/cpsc'.length)) || 'create';
    setActiveSection('cpsc');
    setCpscView(view);
  }, [route, canManageCpscSettings]);

  // Selecting a CPSC view drives the hash so the view is linkable; leaving CPSC clears the
  // sub-route (other sections keep their pre-CHA-267 plain-state behavior, hash stays #/imports).
  const selectCpscView = (view) => {
    setCpscView(view);
    if (navigateTo) navigateTo(`imports/cpsc/${view}`);
  };
  const selectSection = (id) => {
    setActiveSection(id);
    if (!navigateTo) return;
    if (id === 'cpsc') navigateTo(`imports/cpsc/${cpscView}`);
    else navigateTo('imports');
  };

  // Top-level Imports tabs (non-CPSC unchanged; the 10 flat CPSC tabs collapse to one admin-gated entry)
  const importTabs = [
    // CHA-338 — AC2 is the primary "Actual Cost" from 7/1/26; the legacy report is parked under Admin → Archive.
    { id: 'actual-cost-2', label: 'Actual Cost', icon: DollarSign, color: 'green' },
    { id: 'first-sale', label: 'First Sale', icon: TrendingUp, color: 'blue' },
    { id: 'production-tracking', label: 'Production Tracking', icon: Ship, color: 'cyan' },
    { id: 'production-tracking-beta', label: 'Prod Tracking (Beta)', icon: Ship, color: 'violet' },
    { id: 'pipeline-tracker', label: 'Pipeline Tracker', icon: Ship, color: 'emerald' },
    { id: 'tariff-tracker', label: 'Tariff Tracker', icon: Receipt, color: 'amber' },
    ...(canManageCpscSettings ? [{ id: 'cpsc', label: 'CPSC', icon: Shield, color: 'orange' }] : []),
  ];
  // CPSC Settings third-level strip
  const cpscSettingsTabs = [
    { id: 'cpsc-settings', label: 'CPSC Settings', icon: FileCheck, color: 'orange' },
    { id: 'ior-config', label: 'IOR Configuration', icon: Building2, color: 'orange' },
    { id: 'email-recipients', label: 'Email Recipients', icon: Mail, color: 'orange' },
    { id: 'mappings', label: 'Mappings & Templates', icon: Database, color: 'orange' },
  ];
  // Per-CPSC-view header blurb (CHA-267 \u2014 keyed by the new views; create/decode's blurb merges the
  // old exemptions wording with the drop-doc framing, the rest carry their old surface's wording)
  const cpscDesc = {
    'create': 'Create a certificate or exemption \u2014 pick an input method (all four end in operator review)',
    'create/decode': "Drop a lab report / exemption doc \u2014 decode, review, and file (per-style 16 CFR 1610.1(d) exemptions; children's always require a CPC)",
    'create/bv': 'Upload Bureau Veritas test reports for CPSC eFiling',
    'create/manual': 'Enter a CoC by hand or upload the ChainTo template (non-BV labs)',
    'create/link': "Manually link a production style to a real cert/report when auto-matching can't (the style then reads the linked cert's actual status)",
    'drafts': 'Assemble, review, and confirm CPSC certificate drafts',
    'register': 'Every certificate, exemption, and cert link in one filterable place',
    'submissions': 'Drafts in flight at CPSC + the per-style status checker',
    'broker-handoff': 'CPSC certificate identifiers for broker / CBP entry (CSV + email)',
    'audit': 'Read-only audit trail of every CPSC draft, submission, certification, and handoff',
    'settings': 'CPSC eFiling configuration \u2014 credentials, IOR routing, and lookup mappings',
  };
  // Per-Settings-tab blurb (third level) \u2014 preserves the distinct descriptions the flat tabs used to show
  const cpscSettingsDesc = {
    'cpsc-settings': 'CPSC eFiling credentials and connection test',
    'ior-config': 'Importer-of-Record routing and per-IOR credentials',
    'email-recipients': 'Daily broker-report recipients and the master ON/OFF kill switch',
    'mappings': 'CPSC lab + citation mappings and per-client BV field templates',
  };

  return (
    <div className="font-plex text-carbon-ink space-y-5">
      {/* Header \u2014 Carbon (CHA-276): title + per-section blurb, chrome only */}
      <div>
        <h1 className="text-lg font-semibold tracking-[-0.01em] m-0">Imports</h1>
        <p className="text-xs text-carbon-gray-70 mt-1 m-0">
          {activeSection === 'actual-cost-2' && 'Actual vs. system duty, tariff & freight — by shipment and style (ACE + broker lists)'}
          {activeSection === 'first-sale' && 'First sale review and reporting'}
          {activeSection === 'production-tracking' && 'Track production on the water and booking stage'}
          {activeSection === 'tariff-tracker' && 'Tariff and duty analysis by customer, division, and country'}
          {activeSection === 'cpsc' && ((cpscView === 'settings' ? cpscSettingsDesc[cpscSettingsTab] : cpscDesc[cpscView]) || 'CPSC eFiling \u2014 certificates, exemptions, broker handoff, and settings')}
        </p>
      </div>

      {/* Main Section Navigation \u2014 Carbon underline tabs (the CPSC nav inside stays
          on its own in-flight ticket; only this page-level chrome is restyled) */}
      <div className="bg-carbon-white border border-carbon-border">
        <div className="flex border-b border-carbon-border overflow-x-auto">
          {importTabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button key={tab.id} onClick={() => selectSection(tab.id)} className={carbonTabCls(activeSection === tab.id)}>
                <Icon size={14} />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Sub-tabs for First Sale */}
        {activeSection === 'first-sale' && (
          <div className="flex border-b border-carbon-border bg-carbon-gray-10">
            {[
              { id: 'review', label: 'Review', icon: TrendingUp },
              { id: 'vendors', label: 'Vendors', icon: Building2 }, // CHA-362
              { id: 'beneficiaries', label: 'Beneficiaries', icon: Factory }, // CHA-404
              { id: 'savings', label: 'Savings', icon: DollarSign }, // CHA-365
              { id: 'errors', label: 'Errors', icon: AlertTriangle },
              { id: 'settings', label: 'Settings', icon: Settings }
            ].map((tab) => {
              const Icon = tab.icon;
              return (
                <button key={tab.id} onClick={() => setFirstSaleTab(tab.id)} className={carbonSubTabCls(firstSaleTab === tab.id)}>
                  <Icon className="h-3.5 w-3.5" />
                  {tab.label}
                </button>
              );
            })}
          </div>
        )}
        {/* CPSC Create/Manage nav (CHA-267 PR-1 — replaces the CHA-230 9-tab strip; components unchanged) */}
        {activeSection === 'cpsc' && canManageCpscSettings && (
          <>
            <CpscCreateManageNav activeView={cpscView} onSelect={selectCpscView} />
            {cpscView === 'settings' && (
              <CpscTabBar tabs={cpscSettingsTabs} activeId={cpscSettingsTab} onSelect={setCpscSettingsTab} className="bg-slate-50" />
            )}
          </>
        )}
      </div>

      {/* Actual Cost (CHA-323 AC2 — the primary Actual Cost from 7/1/26; legacy report parked under Admin → Archive, CHA-338) */}
      {activeSection === 'actual-cost-2' && (
        <ActualCost2 userProfile={userProfile} tenantId={tenantId} />
      )}

      {/* First Sale Content */}
      {activeSection === 'first-sale' && (
        <>
          {firstSaleTab === 'review' && <FirstSaleReview />}
          {firstSaleTab === 'vendors' && <FirstSaleVendors />}
          {firstSaleTab === 'beneficiaries' && <FirstSaleBeneficiaries />}
          {firstSaleTab === 'savings' && <FirstSaleSavings userProfile={userProfile} />}
          {firstSaleTab === 'errors' && <FirstSaleErrorReport embedded={true} />}
          {firstSaleTab === 'settings' && <FirstSaleSettings />}
        </>
      )}

      {/* Production Tracking Content */}
      {activeSection === 'production-tracking' && (
        <ProductionTrackingDashboard />
      )}

      {/* Production Tracking Beta Content */}
      {activeSection === 'production-tracking-beta' && (
        <ProductionTrackingBetaDashboard />
      )}

      {/* Pipeline Tracker Content */}
      {activeSection === 'pipeline-tracker' && (
        <ProductionPipelineDashboard />
      )}

      {/* Tariff Tracker Content */}
      {activeSection === 'tariff-tracker' && (
        <TariffTrackerDashboard embedded={true} />
      )}

      {/* CPSC content (CHA-267 PR-1) — admin-gated; the Create/Manage view picks the surface.
          BEHAVIOR-FROZEN: every component below is the pre-CHA-267 tab component, unchanged —
          only its home moved (old→new: bv-upload→create/bv · manual-coc→create/manual ·
          exemptions→create/decode · cert-links→create/link · status-checker→submissions ·
          audit-history→audit). */}
      {canManageCpscSettings && activeSection === 'cpsc' && (
        <>
          {cpscView === 'create' && <CpscCreateLanding onSelect={selectCpscView} />}
          {cpscView === 'create/decode' && <CpscExemptions userProfile={userProfile} />}
          {cpscView === 'create/bv' && <CpscUpload userProfile={userProfile} onGoToDrafts={() => selectCpscView('drafts')} />}
          {cpscView === 'create/manual' && <CpscManualCoc userProfile={userProfile} />}
          {cpscView === 'create/link' && <CpscCertLinks userProfile={userProfile} />}
          {cpscView === 'drafts' && <CpscDrafts userProfile={userProfile} />}
          {cpscView === 'register' && <CpscRegister userProfile={userProfile} />}
          {cpscView === 'submissions' && <CpscSubmissions userProfile={userProfile} />}
          {cpscView === 'broker-handoff' && <CpscBrokerHandoff userProfile={userProfile} />}
          {cpscView === 'audit' && <CpscAuditHistory userProfile={userProfile} />}
          {cpscView === 'settings' && (
            <>
              {cpscSettingsTab === 'cpsc-settings' && <CpscSettings userProfile={userProfile} />}
              {cpscSettingsTab === 'ior-config' && <CpscIorConfig userProfile={userProfile} />}
              {cpscSettingsTab === 'email-recipients' && <CpscEmailRecipients userProfile={userProfile} />}
              {cpscSettingsTab === 'mappings' && <CpscMappingsViewer userProfile={userProfile} />}
            </>
          )}
        </>
      )}

    </div>
  );
};

// ============================================================================
// DATA PAGE - Restructured to match Orders/Production style
// ============================================================================
// Exported for the CHA-276 smoke test only (same pattern as ImportsPage above).
export const DataPage = ({ uploadHistory, orderUploadHistory, onUploadComplete, onUploadError, duplicateResults, duplicateChecking, onCheckDuplicates, onRemoveDuplicates, navigateTo, userProfile }) => {
  // CHA-518 / CodeRabbit #385 — resolve the tenant rather than passing a literal into the now
  // tenant-parameterized upload workflows (§8.3). Same resolution the ActualCost2 components use.
  const resolvedTenantId = userProfile?.tenantId || 'inmocean';
  // Main section: 'powerbi', 'importdata', 'test'
  const [activeSection, setActiveSection] = useState('powerbi');
  // Sub-tabs for Test: 'costing', 'orders', 'routing'
  const [testTab, setTestTab] = useState('costing');

  // Build summary data for costing files
  const costingFiles = Object.entries(INMOCEAN_FILE_SCHEMAS)
    .filter(([key, schema]) => schema && schema.type !== 'sync')
    .map(([key, schema]) => ({
      key,
      name: schema.name,
      icon: schema.icon,
      history: uploadHistory[key],
    }));

  // Build summary data for order files
  const orderFiles = Object.entries(INMOCEAN_ORDER_SCHEMAS)
    .filter(([key, schema]) => schema)
    .map(([key, schema]) => ({
      key,
      name: schema.name,
      icon: schema.icon,
      history: orderUploadHistory[key],
    }));

  // Bordered mono status chips mapped ONLY to the REAL upload-history statuses the
  // page already reads (success/completed · processing · error · none). The concept's
  // VERIFIED/QUARANTINE states have no counterpart in this data — not invented here.
  const chip = 'inline-block px-2 py-0.5 font-plex-mono text-[9.5px] tracking-[0.08em] uppercase font-medium border';
  const getStatusBadge = (status) => {
    if (status === 'success' || status === 'completed') {
      return <span className={`${chip} border-carbon-green-50 text-carbon-green-50`}>Synced</span>;
    }
    if (status === 'processing') {
      return <span className={`${chip} border-carbon-blue-60 text-carbon-blue-60`}>Processing</span>;
    }
    if (status === 'error') {
      return <span className={`${chip} border-carbon-red-60 text-carbon-red-60`}>Error</span>;
    }
    return <span className={`${chip} border-carbon-gray-30 text-carbon-gray-50`}>Not uploaded</span>;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const formatNumber = (num) => {
    if (!num && num !== 0) return '—';
    return num.toLocaleString();
  };

  // Section tabs — same ids/order as the old 7-button strip, Carbon underline chrome.
  const dataTabs = [
    { id: 'powerbi', label: 'Power BI', icon: Zap },
    { id: 'powerbi-beta', label: 'Power BI Beta', icon: Zap },
    { id: 'importdata', label: 'Import Data', icon: Ship },
    { id: 'test', label: 'Test', icon: FileText },
    { id: 'warehouse-billing', label: 'Warehouse Billing', icon: Warehouse },
    { id: 'table-status', label: 'Table Status', icon: Activity },
  ];
  // CHA-553 (C, sub-tab): show only the Data tabs the role is granted. Tab id
  // maps 1:1 to the grant `data/<id>`. Allow-by-default (no role doc) and admin
  // see all. `warehouse-billing` has no grantable page id in the role editor
  // (PAGE_GROUPS), so a restricted role never sees it — vocabulary gap noted
  // for the role-editor catalog (CHA-339), not fixed here. UX filter, not a
  // callable boundary (D).
  const visibleDataTabs = dataTabs.filter((t) => isPageGranted(`data/${t.id}`, userProfile?.pages));
  // CHA-553 (C, Codex): derive the shown tab at RENDER time, not in a
  // post-render effect. If the current activeSection is not granted, fall back
  // to the first granted tab; if NONE is granted (e.g. a role with only the
  // `inmocean/data` nav grant and no `data/*` sub-grant), effectiveSection is
  // null and NO tab content renders — never the powerbi default it lacks.
  const effectiveSection = visibleDataTabs.some((t) => t.id === activeSection)
    ? activeSection
    : (visibleDataTabs[0]?.id ?? null);

  return (
    <div className="font-plex text-carbon-ink space-y-5">
      {/* Header — Carbon (CHA-276) */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-semibold tracking-[-0.01em] m-0">Data</h1>
          <p className="text-xs text-carbon-gray-70 mt-1 m-0">
            {effectiveSection === 'powerbi' && 'Power BI update workflow'}
            {effectiveSection === 'powerbi-beta' && 'Power BI Beta - CSV upload testing'}
            {effectiveSection === 'importdata' && 'Import broker data files'}
            {effectiveSection === 'test' && 'File uploads and routing management'}
            {effectiveSection === 'table-status' && 'Table row counts, last upload, and RPT_DATE'}
          </p>
        </div>
      </div>

      {/* Main Section Navigation — Carbon underline tabs */}
      <div className="bg-carbon-white border border-carbon-border">
        <div className="flex border-b border-carbon-border overflow-x-auto">
          {visibleDataTabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button key={tab.id} onClick={() => setActiveSection(tab.id)} className={carbonTabCls(effectiveSection === tab.id)}>
                <Icon size={14} />
                <span className="whitespace-nowrap">{tab.label}</span>
              </button>
            );
          })}
        </div>

        {/* CHA-553 (C, Codex): a role granted the Data nav but no data/* sub-page
            has no visible tab and no content — show a message rather than a blank. */}
        {!effectiveSection && (
          <p className="text-sm text-carbon-gray-70 py-8 text-center">
            No Data pages are assigned to your role.
          </p>
        )}

        {/* Sub-tabs for Test */}
        {effectiveSection === 'test' && (
          <div className="flex border-b border-carbon-border bg-carbon-gray-10">
            {[
              { id: 'costing', label: 'Costing Files', icon: DollarSign },
              { id: 'orders', label: 'Order Files', icon: Package },
              { id: 'routing', label: 'Routing', icon: Navigation }
            ].map((tab) => {
              const Icon = tab.icon;
              return (
                <button key={tab.id} onClick={() => setTestTab(tab.id)} className={carbonSubTabCls(testTab === tab.id)}>
                  <Icon className="h-3.5 w-3.5" />
                  {tab.label}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Content */}
      {effectiveSection === 'powerbi' && (
        <PowerBIWorkflow tenantId={resolvedTenantId} />
      )}

      {effectiveSection === 'powerbi-beta' && (
        <PowerBIBetaWorkflow tenantId={resolvedTenantId} />
      )}

      {effectiveSection === 'importdata' && (
        <ActualCostWorkflow tenantId={resolvedTenantId} />
      )}

      {effectiveSection === 'warehouse-billing' && (
        <WarehouseBillingWorkflow tenantId={resolvedTenantId} />
      )}

      {effectiveSection === 'test' && (
        <>
          {testTab === 'costing' && (
            <div className="space-y-6">
              {/* Smart Drop Zone */}
              <SmartDropZone
                onUploadComplete={(fileType, result) => onUploadComplete(fileType, result, false)}
                onUploadError={(fileType, error) => onUploadError(fileType, error, false)}
                uploadHistory={uploadHistory}
              />

              {/* Summary Table — Carbon (real statuses from upload history only) */}
              <div className="bg-carbon-white border border-carbon-border overflow-hidden">
                <div className="px-[18px] py-2.5 bg-carbon-gray-10 border-b border-carbon-border">
                  <span className="font-plex-mono text-[10.5px] tracking-[0.12em] uppercase font-semibold">Costing files summary</span>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr>
                        <th className={`${C_TH} text-left`}>File Type</th>
                        <th className={`${C_TH} text-center`}>Status</th>
                        <th className={`${C_TH} text-right`}>Records</th>
                        <th className={`${C_TH} text-right`}>Last Upload</th>
                      </tr>
                    </thead>
                    <tbody>
                      {costingFiles.map(({ key, name, icon, history }) => (
                        <tr key={key} className="border-b border-carbon-gray-10 hover:bg-carbon-gray-10">
                          <td className="px-3 py-1.5">
                            <span className="flex items-center gap-2">
                              <span>{icon}</span>
                              <span className="text-[13px] text-carbon-ink">{name}</span>
                            </span>
                          </td>
                          <td className="px-3 py-1.5 text-center">{getStatusBadge(history?.status)}</td>
                          <td className={`px-3 py-1.5 text-right ${C_MONO} text-carbon-gray-70`}>{formatNumber(history?.rowCount)}</td>
                          <td className={`px-3 py-1.5 text-right ${C_MONO} text-carbon-gray-50`}>{formatDate(history?.lastUpload)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Individual File Cards - Collapsible */}
              <details className="group">
                <summary className="flex items-center gap-2 cursor-pointer text-xs text-carbon-gray-50 hover:text-carbon-ink">
                  <ChevronRight size={16} className="transition-transform group-open:rotate-90" />
                  <span>Upload files individually (legacy method)</span>
                </summary>
                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {costingFiles.map(({ key, name, icon, history }) => {
                    const schema = INMOCEAN_FILE_SCHEMAS[key];
                    const isMultiTab = schema.type === 'multi_tab';
                    return (
                      <FileCardWrapper
                        key={key}
                        icon={icon}
                        name={name}
                        description={schema.description}
                        history={history}
                      >
                        {isMultiTab ? (
                          <MultiTabFileUpload
                            fileType={key}
                            schema={schema}
                            onUploadComplete={(result) => onUploadComplete(key, result, false)}
                            onUploadError={(error) => onUploadError(key, error, false)}
                          />
                        ) : (
                          <FileUploadCard
                            fileType={key}
                            schema={schema}
                            onUploadComplete={(result) => onUploadComplete(key, result, false)}
                            onUploadError={(error) => onUploadError(key, error, false)}
                          />
                        )}
                      </FileCardWrapper>
                    );
                  })}
                </div>
              </details>
            </div>
          )}

          {testTab === 'orders' && (
            <div className="space-y-6">
              {/* Summary Table — Carbon (real statuses from upload history only) */}
              <div className="bg-carbon-white border border-carbon-border overflow-hidden">
                <div className="px-[18px] py-2.5 bg-carbon-gray-10 border-b border-carbon-border">
                  <span className="font-plex-mono text-[10.5px] tracking-[0.12em] uppercase font-semibold">Order files summary</span>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr>
                        <th className={`${C_TH} text-left`}>File Type</th>
                        <th className={`${C_TH} text-center`}>Status</th>
                        <th className={`${C_TH} text-right`}>Records</th>
                        <th className={`${C_TH} text-right`}>Last Upload</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orderFiles.map(({ key, name, icon, history }) => (
                        <tr key={key} className="border-b border-carbon-gray-10 hover:bg-carbon-gray-10">
                          <td className="px-3 py-1.5">
                            <span className="flex items-center gap-2">
                              <span>{icon}</span>
                              <span className="text-[13px] text-carbon-ink">{name}</span>
                            </span>
                          </td>
                          <td className="px-3 py-1.5 text-center">{getStatusBadge(history?.status)}</td>
                          <td className={`px-3 py-1.5 text-right ${C_MONO} text-carbon-gray-70`}>{formatNumber(history?.rowCount)}</td>
                          <td className={`px-3 py-1.5 text-right ${C_MONO} text-carbon-gray-50`}>{formatDate(history?.lastUpload)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* File Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {orderFiles.map(({ key, name, icon, history }) => {
                  const schema = INMOCEAN_ORDER_SCHEMAS[key];
                  return (
                    <FileCardWrapper
                      key={key}
                      icon={icon}
                      name={name}
                      description={schema.description}
                      history={history}
                    >
                      <FileUploadCard
                        fileType={key}
                        schema={schema}
                        onUploadComplete={(result) => onUploadComplete(key, result, true)}
                        onUploadError={(error) => onUploadError(key, error, true)}
                      />
                    </FileCardWrapper>
                  );
                })}
              </div>
            </div>
          )}

          {testTab === 'routing' && (
            <RoutingSheetImporter />
          )}
        </>
      )}

      {effectiveSection === 'table-status' && (
        <TableStatusView />
      )}
    </div>
  );
};


// ============================================================================
// TABLE STATUS VIEW
// ============================================================================
const AzureSyncStatusPanel = () => {
  const [syncData, setSyncData] = useState(null);
  const [syncing, setSyncing] = useState(false);

  const loadStatus = useCallback(async () => {
    try {
      const functions = getFunctions();
      const result = await httpsCallable(functions, 'getAzureSyncStatus')();
      setSyncData(result.data?.files || []);
    } catch (e) { console.warn('Could not load Azure sync status:', e.message); }
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  const triggerSync = async () => {
    setSyncing(true);
    try {
      const functions = getFunctions();
      await httpsCallable(functions, 'syncAzureData', { timeout: 570000 })({ force: true });
      await loadStatus();
    } catch (e) { console.error('Sync failed:', e); }
    finally { setSyncing(false); }
  };

  if (!syncData) return null;

  const fmtTs = (ts) => {
    if (!ts) return 'Never';
    const d = new Date(ts);
    const hours = (Date.now() - d.getTime()) / 3600000;
    const timeStr = d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    if (hours < 1) return `${Math.round(hours * 60)}m ago`;
    if (hours < 24) return `${Math.round(hours)}h ago (${timeStr})`;
    return timeStr;
  };

  // Live status dots (round dots are the sanctioned radius exception) mapped to the
  // panel's real per-file sync states: success / syncing / error / none.
  const statusIcon = (s) => {
    if (s === 'success') return 'bg-carbon-green-50';
    if (s === 'syncing') return 'bg-carbon-orange-40 animate-pulse';
    if (s === 'error') return 'bg-carbon-red-60';
    return 'bg-carbon-gray-30';
  };

  const powerbi = syncData.filter(f => f.workflow === 'powerbi');
  const target = syncData.filter(f => f.workflow === 'target');

  const groupRow = (label) => (
    <tr>
      <td colSpan={7} className="px-3 py-1.5 bg-carbon-gray-10 border-b border-carbon-border font-plex-mono text-[9.5px] tracking-[0.12em] uppercase font-semibold text-carbon-gray-70">{label}</td>
    </tr>
  );

  return (
    <div className="bg-carbon-white border border-carbon-border overflow-hidden">
      <div className="px-[18px] py-2.5 bg-carbon-gray-10 border-b border-carbon-border flex items-center justify-between gap-2 flex-wrap">
        <div>
          <span className="font-plex-mono text-[10.5px] tracking-[0.12em] uppercase font-semibold">Azure Data Lake sync</span>
          <p className="text-[11px] text-carbon-gray-50 m-0 mt-0.5">Auto-sync Mon-Fri 8:10am, 9:10am, 10:10am, 12:10pm, 2:10pm ET</p>
        </div>
        <button onClick={triggerSync} disabled={syncing}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-carbon-ink text-carbon-white hover:bg-carbon-gray-80 disabled:opacity-50">
          <RefreshCw size={12} className={syncing ? 'animate-spin' : ''} />
          {syncing ? 'Syncing All...' : 'Force Sync All'}
        </button>
      </div>
      <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead>
          <tr>
            <th className={`${C_TH} text-left`}>Step</th>
            <th className={`${C_TH} text-left`}>File</th>
            <th className={`${C_TH} text-left`}>Table</th>
            <th className={`${C_TH} text-right`}>Rows</th>
            <th className={`${C_TH} text-left`}>Last Sync</th>
            <th className={`${C_TH} text-left`}>Azure Modified</th>
            <th className={`${C_TH} text-center`}>Status</th>
          </tr>
        </thead>
        <tbody>
          {powerbi.length > 0 && groupRow('Power BI Beta')}
          {powerbi.map(f => (
            <tr key={f.key} className="border-b border-carbon-gray-10 hover:bg-carbon-gray-10">
              <td className="px-3 py-1.5"><span className="font-plex-mono text-[10px] font-medium border border-carbon-gray-30 text-carbon-gray-70 px-1.5 py-0.5">{f.step}</span></td>
              <td className="px-3 py-1.5 text-[13px] font-medium text-carbon-ink">{f.label}</td>
              <td className={`px-3 py-1.5 font-plex-mono text-[10px] text-carbon-gray-50`}>{f.tableName}</td>
              <td className={`px-3 py-1.5 text-right ${C_MONO} text-carbon-gray-70`}>{f.rowCount ? Number(f.rowCount).toLocaleString() : '—'}</td>
              <td className={`px-3 py-1.5 ${C_MONO} text-carbon-gray-70`}>{fmtTs(f.lastSync)}</td>
              <td className={`px-3 py-1.5 ${C_MONO} text-carbon-gray-50`}>{fmtTs(f.azureLastModified)}</td>
              <td className="px-3 py-1.5 text-center"><span className={`w-2.5 h-2.5 rounded-full inline-block ${statusIcon(f.status)}`} /></td>
            </tr>
          ))}
          {target.length > 0 && groupRow('Target Balance Sheet')}
          {target.map(f => (
            <tr key={f.key} className="border-b border-carbon-gray-10 hover:bg-carbon-gray-10">
              <td className="px-3 py-1.5"><span className="font-plex-mono text-[10px] font-medium border border-carbon-gray-30 text-carbon-gray-70 px-1.5 py-0.5">{f.step}</span></td>
              <td className="px-3 py-1.5 text-[13px] font-medium text-carbon-ink">{f.label}</td>
              <td className={`px-3 py-1.5 font-plex-mono text-[10px] text-carbon-gray-50`}>{f.tableName}</td>
              <td className={`px-3 py-1.5 text-right ${C_MONO} text-carbon-gray-70`}>{f.rowCount ? Number(f.rowCount).toLocaleString() : '—'}</td>
              <td className={`px-3 py-1.5 ${C_MONO} text-carbon-gray-70`}>{fmtTs(f.lastSync)}</td>
              <td className={`px-3 py-1.5 ${C_MONO} text-carbon-gray-50`}>{fmtTs(f.azureLastModified)}</td>
              <td className="px-3 py-1.5 text-center"><span className={`w-2.5 h-2.5 rounded-full inline-block ${statusIcon(f.status)}`} /></td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
      {syncData.some(f => f.lastError) && (
        <div className="relative px-[18px] py-2.5 border-t border-carbon-border">
          <span className="absolute left-0 top-0 bottom-0 w-[3px] bg-carbon-red-60" />
          {syncData.filter(f => f.lastError).map(f => (
            <p key={f.key} className="text-xs text-carbon-red-60 m-0"><strong>{f.label}:</strong> {f.lastError}</p>
          ))}
        </div>
      )}
    </div>
  );
};

const TableStatusView = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchStatus = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const functions = getFunctions();
      const getTableStatus = httpsCallable(functions, 'getTableStatus');
      const result = await getTableStatus();
      setData(result.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const formatTs = (ts) => {
    if (!ts) return '—';
    const d = new Date(ts);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  // Freshness color on the REAL timestamps the callable already returns
  // (<24h fresh · <7d aging · older stale) — Carbon severity colors.
  const freshnessClass = (ts) => {
    if (!ts) return 'text-carbon-gray-40';
    const hours = (Date.now() - new Date(ts).getTime()) / 3600000;
    if (hours < 24) return 'text-carbon-green-50';
    if (hours < 168) return 'text-carbon-orange-70';
    return 'text-carbon-red-60';
  };

  if (loading) return (
    <div className="bg-carbon-white border border-carbon-border">
      <SkeletonRows rows={6} />
    </div>
  );

  if (error) return (
    <ErrorBanner what="Table status failed to load." staleNote={error} onRetry={fetchStatus} />
  );

  return (
    <div className="space-y-4 font-plex text-carbon-ink">
      <div className="flex justify-end">
        <button onClick={fetchStatus} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-carbon-ink text-carbon-white hover:bg-carbon-gray-80">
          <RefreshCw size={12} /> Refresh
        </button>
      </div>
      <AzureSyncStatusPanel />
      {data && Object.entries(data).map(([group, tables]) => (
        <div key={group} className="bg-carbon-white border border-carbon-border overflow-hidden">
          <div className="px-[18px] py-2.5 bg-carbon-gray-10 border-b border-carbon-border">
            <span className="font-plex-mono text-[10.5px] tracking-[0.12em] uppercase font-semibold">{group}</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr>
                  <th className={`${C_TH} text-left`}>Table</th>
                  <th className={`${C_TH} text-right`}>Rows</th>
                  <th className={`${C_TH} text-right`}>Last Modified</th>
                  <th className={`${C_TH} text-right`}>Last Upload</th>
                  <th className={`${C_TH} text-right`}>RPT_DATE</th>
                </tr>
              </thead>
              <tbody>
                {tables.map((t) => (
                  <tr key={t.table} className="border-b border-carbon-gray-10 hover:bg-carbon-gray-10">
                    <td className={`px-3 py-1.5 ${C_MONO} text-carbon-ink`}>{t.table}</td>
                    <td className={`px-3 py-1.5 text-right ${C_MONO} text-carbon-gray-70`}>{t.row_count != null ? t.row_count.toLocaleString() : '—'}</td>
                    <td className={`px-3 py-1.5 text-right ${C_MONO} ${freshnessClass(t.last_modified)}`}>{formatTs(t.last_modified)}</td>
                    <td className={`px-3 py-1.5 text-right ${C_MONO} ${freshnessClass(t.last_upload)}`}>{formatTs(t.last_upload)}</td>
                    <td className={`px-3 py-1.5 text-right ${C_MONO} text-carbon-gray-70`}>{t.rpt_date || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
};

const TabButton = ({ active, onClick, icon: Icon, label }) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 -mb-px transition-colors ${
      active
        ? 'border-blue-500 text-blue-600'
        : 'border-transparent text-slate-500 hover:text-slate-700'
    }`}
  >
    <Icon size={18} />
    {label}
  </button>
);

// File Card Wrapper - wraps FileUploadCard with proper header
// CHA-276 — Carbon square upload card: bordered mono status chip on the card's
// real upload-history statuses + mono "last upload" meta line. Chrome only.
const FileCardWrapper = ({ icon, name, description, history, children }) => {
  const chip = 'font-plex-mono text-[9.5px] tracking-[0.08em] uppercase font-medium border px-2 py-0.5';
  const getStatusBadge = () => {
    if (!history) return null;
    if (history.status === 'success' || history.status === 'completed') {
      return <span className={`${chip} border-carbon-green-50 text-carbon-green-50`}>Synced</span>;
    }
    if (history.status === 'processing') {
      return <span className={`${chip} border-carbon-blue-60 text-carbon-blue-60`}>Processing</span>;
    }
    if (history.status === 'error') {
      return <span className={`${chip} border-carbon-red-60 text-carbon-red-60`}>Error</span>;
    }
    return null;
  };

  const getLastUpload = () => {
    if (!history?.lastUpload) return null;
    const date = new Date(history.lastUpload);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="bg-carbon-white border border-carbon-border overflow-hidden font-plex text-carbon-ink">
      {/* Header */}
      <div className="px-4 py-3 border-b border-carbon-border bg-carbon-gray-10">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-lg">{icon}</span>
            <h3 className="font-semibold text-carbon-ink text-[13px] m-0">{name}</h3>
          </div>
          {getStatusBadge()}
        </div>
        {description && (
          <p className="text-[11px] text-carbon-gray-50 mt-1 m-0">{description}</p>
        )}
        {getLastUpload() && (
          <p className="font-plex-mono text-[10px] text-carbon-gray-50 mt-1 m-0 uppercase">Last: {getLastUpload()}</p>
        )}
      </div>
      {/* Upload Area */}
      <div className="p-3">
        {children}
      </div>
    </div>
  );
};

// ============================================================================
// SHARED COMPONENTS
// ============================================================================
const ReportCard = ({ title, description, icon: Icon, color, badge, onClick }) => {
  const colors = {
    blue: 'hover:border-blue-300',
    green: 'hover:border-green-300',
    orange: 'hover:border-orange-300',
    purple: 'hover:border-purple-300',
    indigo: 'hover:border-indigo-300',
  };

  const iconColors = {
    blue: 'text-blue-500 bg-blue-100',
    green: 'text-green-500 bg-green-100',
    orange: 'text-orange-500 bg-orange-100',
    purple: 'text-purple-500 bg-purple-100',
    indigo: 'text-indigo-500 bg-indigo-100',
  };

  const badgeColors = {
    ERRORS: 'bg-orange-100 text-orange-700',
    NEW: 'bg-purple-100 text-purple-700',
  };

  return (
    <button
      onClick={onClick}
      className={`p-6 rounded-xl border border-slate-200 bg-white text-left transition-all group hover:shadow-lg ${colors[color]}`}
    >
      <div className="flex items-start justify-between mb-4">
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${iconColors[color]}`}>
          <Icon size={24} />
        </div>
        {badge && (
          <span className={`text-[10px] font-semibold px-2 py-1 rounded-full ${badgeColors[badge]}`}>
            {badge}
          </span>
        )}
      </div>
      <h3 className="font-semibold text-slate-800 text-lg mb-2">{title}</h3>
      <p className="text-sm text-slate-500 leading-relaxed">{description}</p>
      <div className="mt-4 text-sm text-slate-400 flex items-center gap-1 group-hover:text-slate-600">
        Open <ChevronRight size={14} />
      </div>
    </button>
  );
};
